// ============================================================================
// DokumentPublik.jsx — një faqe, çdo dokument juridik. Pa asnjë tekst brenda.
//
// 30 gusht 2026.
//
// ─────────────────────────────────────────────────────────────────────────────
// PSE U FSHI TEKSTI I SHKRUAR ME DORË
//
// «Faqja /privacy duhet të fshihet, nuk guxojmë më të kemi hardcode. Kjo duhet
//  të dalë nga «Rreth platformës», dokumenti i ngarkuar.»
//
// Deri sot politika e privatësisë jetonte në tre vende: te tabela `documents`,
// te `dokumentet/*.docx`, dhe e shkruar me dorë brenda `PrivacyPolicy.jsx`.
// Tre kopje do të thotë se dy prej tyre janë të gabuara, dhe askush nuk e di
// cila — pikërisht ajo që ndodhi: faqja publike mbeti një draft i 3 gushtit
// ndërsa dokumenti i vërtetë ishte ngarkuar prej kohësh.
//
// Tani teksti vjen VETËM nga `documents`, përmes `rpc_publik_dokument`.
// Ngarkon një version të ri te Admin Center-i → faqja e tregon menjëherë.
// Asnjë deploy, asnjë rresht kodi, asnjë kopje që vjetrohet.
//
// ─────────────────────────────────────────────────────────────────────────────
// PSE NUK MJAFTONTE SISTEMI EKZISTUES
//
// Ai ekzistonte, po çdo funksion i tij ishte `to authenticated`. Google Play e
// kërkon URL-në e privatësisë të hapshme PA LLOGARI: rishikuesi nuk regjistrohet.
// Prandaj `PUBLIK1_dokumentet_publike.sql` — një derë e ngushtë, vetëm lexim,
// vetëm dokumentet e platformës, vetëm llojet juridike.
//
// ─────────────────────────────────────────────────────────────────────────────
// GJUHA THUHET, NUK FSHIHET
//
// Nëse dokumenti s'ekziston në gjuhën e kërkuar, RPC-ja kthen shqipen DHE e
// shënon te `gjuha_e_kthyer`. Faqja e shkruan hapur. Një dokument juridik i
// dorëzuar në gjuhë tjetër pa e thënë është më keq se mungesa e tij — njeriu
// mendon se ka lexuar atë që i takonte.
//
// ─────────────────────────────────────────────────────────────────────────────
// HTML-JA VJEN NGA WORD
//
// `content_html` prodhohet nga mammoth kur ngarkohet .docx-i, te Admin Center,
// nga një administrator platforme. Pra burimi është i besuar — nuk është tekst
// i shkruar nga përdorues. Prandaj shfaqet me `dangerouslySetInnerHTML`, dhe
// kjo është e vetmja arsye pse lejohet këtu.
// ============================================================================

import { useEffect, useState } from "react";
import { supabase } from "./supabaseClient";

const LLOJET = {
  privacy:   { sq: "Politika e Privatësisë", en: "Privacy Policy" },
  terms:     { sq: "Kushtet e Përdorimit",   en: "Terms of Use" },
  cookies:   { sq: "Cookies dhe gjurmimi",   en: "Cookies and tracking" },
  us_notice: { sq: "Njoftim për banorët e SHBA-së", en: "Notice for US residents" },
  info:      { sq: "Rreth platformës",       en: "About the platform" },
};

const F = {
  sq: {
    duke: "Duke ngarkuar…",
    smund: "Ky dokument nuk u ngarkua dot. Provo sërish më vonë ose na shkruaj te",
    smungon: "Ky dokument nuk është publikuar ende.",
    rene: "Ky dokument nuk është ende në gjuhën tuaj. Po e shihni në shqip, versionin origjinal.",
    version: "Versioni",
    perditesuar: "Përditësuar",
    tjera: "Dokumente të tjera",
  },
  en: {
    duke: "Loading…",
    smund: "This document could not be loaded. Please try again later, or write to us at",
    smungon: "This document has not been published yet.",
    rene: "This document is not yet available in your language. It is shown in Albanian, the original version.",
    version: "Version",
    perditesuar: "Updated",
    tjera: "Other documents",
  },
};

const KONTAKTI = "kontakt@pemaefamiljes.com";

export default function DokumentPublik({ lloji = "privacy" }) {
  const [gj, setGj] = useState(() => {
    try { return /^sq/i.test(navigator.language || "") ? "sq" : "en"; } catch (e) { return "sq"; }
  });
  const [dok, setDok] = useState(null);
  const [gjuhet, setGjuhet] = useState([]);
  const [gjendja, setGjendja] = useState("duke"); // duke | gati | bosh | gabim
  const t = F[gj] || F.sq;

  useEffect(() => {
    let gjalle = true;
    setGjendja("duke");
    (async () => {
      try {
        const { data, error } = await supabase.rpc("rpc_publik_dokument", { p_lloji: lloji, p_gjuha: gj });
        if (!gjalle) return;
        if (error) { setGjendja("gabim"); return; }
        const rr = Array.isArray(data) ? data[0] : data;
        if (!rr || !rr.content_html) { setDok(null); setGjendja("bosh"); return; }
        setDok(rr); setGjendja("gati");
      } catch (e) { if (gjalle) setGjendja("gabim"); }
    })();
    return () => { gjalle = false; };
  }, [lloji, gj]);

  // Çelësi i gjuhës ofrohet vetëm kur ka vërtet ku të kalojë.
  useEffect(() => {
    let gjalle = true;
    (async () => {
      try {
        const { data } = await supabase.rpc("rpc_publik_gjuhet_e_dokumentit", { p_lloji: lloji });
        if (gjalle && Array.isArray(data)) setGjuhet(data.map((r) => r.lang));
      } catch (e) { /* çelësi thjesht nuk shfaqet */ }
    })();
    return () => { gjalle = false; };
  }, [lloji]);

  const titulli = (LLOJET[lloji] || {})[gj] || (LLOJET[lloji] || {}).sq || "";
  const raneNeShqip = dok && dok.gjuha_e_kthyer && dok.gjuha_e_kerkuar &&
                      dok.gjuha_e_kthyer !== dok.gjuha_e_kerkuar;

  return (
    <div style={S.faqja}>
      <div style={S.koka}>
        <div>
          <h1 style={S.h1}>{titulli}</h1>
          {dok && (
            <p style={S.meta}>
              {t.version} {dok.version} · {t.perditesuar}{" "}
              {new Date(dok.uploaded_at).toLocaleDateString(gj === "sq" ? "sq-AL" : "en-GB")}
            </p>
          )}
        </div>
        {gjuhet.length > 1 && (
          <div style={S.çelësi} role="group" aria-label="Language">
            {gjuhet.map((g) => (
              <button key={g} onClick={() => setGj(g)}
                style={{ ...S.gjButon, ...(gj === g ? S.gjAktiv : null) }}>
                {g === "sq" ? "Shqip" : g === "en" ? "English" : g.toUpperCase()}
              </button>
            ))}
          </div>
        )}
      </div>

      {raneNeShqip && <div style={S.shenim}>{t.rene}</div>}

      {gjendja === "duke" && <p style={S.gri}>{t.duke}</p>}
      {gjendja === "bosh" && <p style={S.gri}>{t.smungon}</p>}
      {gjendja === "gabim" && (
        <p style={S.gri}>{t.smund} <a href={`mailto:${KONTAKTI}`} style={S.lidhja}>{KONTAKTI}</a></p>
      )}
      {gjendja === "gati" && dok && (
        // Burimi është një .docx i ngarkuar nga administrator platforme dhe i
        // kthyer në HTML te serveri — jo tekst përdoruesi. Shih kokën.
        <div style={S.trupi} dangerouslySetInnerHTML={{ __html: dok.content_html }} />
      )}

      <p style={S.fund}>
        <a href="/privacy" style={S.lidhja}>{LLOJET.privacy[gj]}</a>
        {" · "}
        <a href="/terms" style={S.lidhja}>{LLOJET.terms[gj]}</a>
        {" · "}
        <a href="/delete-account" style={S.lidhja}>
          {gj === "sq" ? "Fshirja e llogarisë" : "Delete your account"}
        </a>
      </p>
    </div>
  );
}

const S = {
  faqja: { maxWidth: 760, margin: "0 auto", padding: "40px 20px 72px", fontFamily: "system-ui, -apple-system, 'Segoe UI', sans-serif", color: "#2A2118", lineHeight: 1.72, background: "#fff" },
  koka: { display: "flex", flexWrap: "wrap", gap: 16, alignItems: "flex-start", justifyContent: "space-between", borderBottom: "1px solid #E7E2D6", paddingBottom: 20, marginBottom: 26 },
  h1: { fontSize: 27, margin: 0, letterSpacing: "-0.01em" },
  meta: { color: "#8A8272", fontSize: 13, margin: "4px 0 0" },
  çelësi: { display: "flex", border: "1px solid #DFD8C6", borderRadius: 8, overflow: "hidden", flexShrink: 0 },
  gjButon: { padding: "7px 14px", fontSize: 13.5, background: "#fff", border: "none", cursor: "pointer", color: "#6B6152", fontFamily: "inherit" },
  gjAktiv: { background: "#8A1A24", color: "#fff", fontWeight: 600 },
  shenim: { background: "#FFF8E1", border: "1px solid #F0D98C", borderRadius: 8, padding: "12px 16px", marginBottom: 24, fontSize: 13.5, color: "#5A4A22" },
  gri: { fontSize: 14.5, color: "#6B6152" },
  trupi: { fontSize: 15, color: "#3A342C" },
  lidhja: { color: "#8A6A1F" },
  fund: { marginTop: 40, paddingTop: 20, borderTop: "1px solid #E7E2D6", fontSize: 13.5, color: "#8A8272" },
};
